import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Table,
  Card,
  Button,
  Space,
  Input,
  Select,
  Tag,
  Dropdown,
  Modal,
  Form,
  message,
  Tooltip,
  Progress,
  Upload,
  Checkbox,
} from 'antd'
import type { MenuProps, TableProps, UploadProps } from 'antd'
import {
  PlusOutlined,
  SearchOutlined,
  FilterOutlined,
  ExportOutlined,
  ImportOutlined,
  ReloadOutlined,
  MoreOutlined,
  SendOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  StarOutlined,
  InboxOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined,
  DownloadOutlined,
  WarningOutlined,
  CheckOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { useProjects, useCreateProject, useDeleteProject, useBatchRescore, useBatchDelete, useMoveToColdPool, useRestoreFromColdPool } from '../hooks'
import { useProjectFilterStore } from '../store'
import { projectApi, scrapeApi, crmApi, authApi } from '../api/client'
import type { Project, ProjectStatus, ScrapeStatus } from '../types'

const { Search } = Input

// 状态配置
const statusConfig: Record<ProjectStatus, { color: string; text: string }> = {
  new: { color: 'default', text: '新项目' },
  scored: { color: 'blue', text: '已评分' },
  queued: { color: 'cyan', text: '待触达' },
  phase1_pending: { color: 'geekblue', text: 'P1待发' },
  phase1_sent: { color: 'purple', text: 'P1已发' },
  phase2_pending: { color: 'magenta', text: 'P2待发' },
  phase2_sent: { color: 'orange', text: 'P2已发' },
  phase3_pending: { color: 'gold', text: 'P3待发' },
  phase3_sent: { color: 'lime', text: 'P3已发' },
  replied: { color: 'green', text: '已回复' },
  interested: { color: 'success', text: '有意向' },
  meeting_scheduled: { color: 'processing', text: '已约会' },
  converted: { color: 'success', text: '已转化' },
  rejected: { color: 'error', text: '已拒绝' },
  cold_pool: { color: 'default', text: '冷池' },
}

function ProjectList() {
  const navigate = useNavigate()
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [selectAllPages, setSelectAllPages] = useState(false)  // 跨页全选
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [form] = Form.useForm()

  // Quick scrape state
  const [quickScrapeModalOpen, setQuickScrapeModalOpen] = useState(false)
  const [scrapingProject, setScrapingProject] = useState<Project | null>(null)
  const [selectedScrapeTypes, setSelectedScrapeTypes] = useState<string[]>([])
  const [scraping, setScraping] = useState(false)

  // CRM conflict check state
  const [conflictChecking, setConflictChecking] = useState<Record<string, boolean>>({})

  // GBR check state for create form
  const [gbrChecking, setGbrChecking] = useState(false)
  const [gbrResult, setGbrResult] = useState<{ allowed: boolean; crm_bd?: string } | null>(null)

  // Sync / Import from CRM state
  const [syncingFromCrm, setSyncingFromCrm] = useState(false)
  const [importingFromCrm, setImportingFromCrm] = useState(false)
  const [importResult, setImportResult] = useState<{
    total_from_crm: number
    created: number
    updated: number
    skipped_conflict: number
    errors: number
    conflict_items: Array<{ twitter_handle: string; bb_owner: string }>
    created_items: Array<{ twitter_handle: string; project_name: string }>
  } | null>(null)

  // 状态管理
  const {
    status,
    searchText,
    scrapeFilter,
    crmConflictFilter,
    sortBy,
    sortOrder,
    page,
    pageSize,
    setFilter,
    resetFilters,
    setPage,
    setPageSize,
  } = useProjectFilterStore()

  // 数据查询
  const { data, isLoading, refetch } = useProjects({
    status: status || undefined,
    search: searchText || undefined,
    scrape_filter: scrapeFilter || undefined,
    crm_conflict_status: crmConflictFilter || undefined,
    sort_by: sortBy,
    sort_order: sortOrder,
    page,
    page_size: pageSize,
  })

  // Mutations
  const createProject = useCreateProject()
  const deleteProject = useDeleteProject()
  const batchRescore = useBatchRescore()
  const batchDelete = useBatchDelete()
  const moveToColdPool = useMoveToColdPool()
  const restoreFromColdPool = useRestoreFromColdPool()

  // Quick scrape handler
  const handleQuickScrape = async () => {
    if (!scrapingProject || selectedScrapeTypes.length === 0) {
      message.warning('请至少选择一种抓取类型')
      return
    }

    setScraping(true)
    try {
      const result = await scrapeApi.batchScrape({
        project_ids: [scrapingProject.id],
        scrape_types: selectedScrapeTypes,
        force: false,
      })

      const successCount = result.success || 0
      const failedCount = result.failed || 0

      if (failedCount > 0 && result.results && result.results.length > 0) {
        const r = result.results[0]
        const messages: string[] = []
        if (r.email) messages.push(`邮箱: ${r.email.message}`)
        if (r.twitter) messages.push(`推特: ${r.twitter.message}`)
        if (r.telegram) messages.push(`Telegram: ${r.telegram.message}`)
        message.warning(`抓取完成，部分失败: ${messages.join(', ')}`)
      } else {
        message.success(`抓取完成！成功: ${successCount}`)
      }

      // Refresh project list
      refetch()
      setQuickScrapeModalOpen(false)
      setScrapingProject(null)
    } catch (error: any) {
      message.error(`抓取失败: ${error.message || '未知错误'}`)
    } finally {
      setScraping(false)
    }
  }

  // Batch scrape selected projects
  const handleBatchScrapeSelected = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择项目')
      return
    }

    // Identify missing types across all selected projects
    const selectedProjects = (data?.items || []).filter(p => selectedRowKeys.includes(p.id))
    const missingTypes = new Set<string>()

    selectedProjects.forEach(p => {
      if (!p.email || p.email_scrape_status === 'NOT_SCRAPED') {
        missingTypes.add('email')
      }
      if (!p.twitter_handle || p.twitter_scrape_status === 'NOT_SCRAPED') {
        missingTypes.add('twitter')
      }
      if (!p.telegram_group || p.telegram_scrape_status === 'NOT_SCRAPED') {
        missingTypes.add('telegram')
      }
    })

    const typesArray = Array.from(missingTypes)

    if (typesArray.length === 0) {
      message.info('所选项目的信息已全部抓取')
      return
    }

    Modal.confirm({
      title: '批量抓取确认',
      content: (
        <div>
          <p>将为 <strong>{selectedRowKeys.length}</strong> 个项目抓取以下信息：</p>
          <Space>
            {typesArray.map(type => (
              <Tag key={type} color="processing">
                {type === 'email' ? '邮箱' : type === 'twitter' ? '推特' : 'Telegram'}
              </Tag>
            ))}
          </Space>
        </div>
      ),
      onOk: async () => {
        setScraping(true)
        try {
          const result = await scrapeApi.batchScrape({
            project_ids: selectedRowKeys.map(String),
            scrape_types: typesArray,
            force: false,
          })

          const successCount = result.success || 0
          const failedCount = result.failed || 0

          if (failedCount > 0) {
            message.warning(`抓取完成！成功: ${successCount}, 失败: ${failedCount}`)
          } else {
            message.success(`抓取完成！成功抓取 ${successCount} 个项目`)
          }

          refetch()
          setSelectedRowKeys([])
        } catch (error: any) {
          message.error(`抓取失败: ${error.message || '未知错误'}`)
        } finally {
          setScraping(false)
        }
      },
    })
  }

  // 表格列定义
  const columns: TableProps<Project>['columns'] = [
    {
      title: '项目名称',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      fixed: 'left',
      render: (name: string, record: Project) => (
        <Space>
          <a onClick={() => navigate(`/projects/${record.id}`)}>{name}</a>
          {record.priority_score && record.priority_score >= 80 && (
            <StarOutlined style={{ color: '#faad14' }} />
          )}
        </Space>
      ),
    },
    {
      title: '网站',
      dataIndex: 'website',
      key: 'website',
      width: 180,
      ellipsis: true,
      render: (url: string) => (
        <Tooltip title={url}>
          <a href={url} target="_blank" rel="noopener noreferrer">
            {url?.replace(/^https?:\/\//, '')}
          </a>
        </Tooltip>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: ProjectStatus) => {
        const config = statusConfig[status]
        return <Tag color={config?.color}>{config?.text || status}</Tag>
      },
    },
    {
      title: '优先级',
      dataIndex: 'priority_score',
      key: 'priority_score',
      width: 120,
      sorter: true,
      render: (score: number) => {
        if (!score) return '-'
        let color = 'default'
        if (score >= 80) color = 'success'
        else if (score >= 60) color = 'warning'
        else if (score >= 40) color = 'processing'
        return (
          <Progress
            percent={score}
            size="small"
            status={color as 'success' | 'exception' | 'normal' | 'active'}
            format={(p) => `${p}`}
          />
        )
      },
    },
    {
      title: 'Twitter',
      dataIndex: 'twitter_handle',
      key: 'twitter_handle',
      width: 120,
      render: (handle: string) =>
        handle ? (
          <a
            href={`https://twitter.com/${handle}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            @{handle}
          </a>
        ) : (
          '-'
        ),
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 100,
      render: (category: string) => category || '-',
    },
    {
      title: '信息状态',
      key: 'scrape_status',
      width: 140,
      render: (_: unknown, record: Project) => {
        const getScrapeIcon = (status?: ScrapeStatus) => {
          if (!status || status === 'NOT_SCRAPED') {
            return <MinusCircleOutlined style={{ color: '#d9d9d9' }} />
          }
          if (status === 'SCRAPED_NO_DATA') {
            return <CloseCircleOutlined style={{ color: '#faad14' }} />
          }
          return <CheckCircleOutlined style={{ color: '#52c41a' }} />
        }

        return (
          <Space size="small">
            <Tooltip title={`邮箱: ${record.email || '未抓取'}`}>
              {getScrapeIcon(record.email_scrape_status)}
            </Tooltip>
            <Tooltip title={`推特: ${record.twitter_handle || '未抓取'}`}>
              {getScrapeIcon(record.twitter_scrape_status)}
            </Tooltip>
            <Tooltip title={`Telegram: ${record.telegram_group || '未抓取'}`}>
              {getScrapeIcon(record.telegram_scrape_status)}
            </Tooltip>
          </Space>
        )
      },
    },
    {
      title: '触达次数',
      dataIndex: 'outreach_count',
      key: 'outreach_count',
      width: 100,
      render: (count: number, record: Project) => (
        <span>
          {count || 0} / P{record.phase || 0}
        </span>
      ),
    },
    {
      title: '下次触达',
      dataIndex: 'next_outreach_at',
      key: 'next_outreach_at',
      width: 120,
      render: (time: string) =>
        time ? dayjs(time).format('MM-DD HH:mm') : '-',
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 120,
      sorter: true,
      render: (time: string) => dayjs(time).format('MM-DD HH:mm'),
    },
    {
      title: 'CRM状态',
      dataIndex: 'crm_conflict_status',
      key: 'crm_conflict_status',
      width: 90,
      render: (status: string) => {
        if (!status || status === 'none') return null
        const crmStatusConfig: Record<string, { color: string; text: string }> = {
          conflict_detected: { color: 'red', text: '冲突' },
          conflict_resolved: { color: 'green', text: '已解决' },
          pending_check: { color: 'orange', text: '检测中' },
          check_failed: { color: 'default', text: '检测失败' },
        }
        const cfg = crmStatusConfig[status]
        return cfg ? <Tag color={cfg.color}>{cfg.text}</Tag> : <Tag>{status}</Tag>
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_: unknown, record: Project) => {
        const menuItems: MenuProps['items'] = [
          {
            key: 'view',
            icon: <EyeOutlined />,
            label: '查看详情',
            onClick: () => navigate(`/projects/${record.id}`),
          },
          {
            key: 'edit',
            icon: <EditOutlined />,
            label: '编辑',
            onClick: () => navigate(`/projects/${record.id}?edit=true`),
          },
          {
            key: 'outreach',
            icon: <SendOutlined />,
            label: '发起触达',
            onClick: () => navigate(`/outreach?project_id=${record.id}`),
          },
          {
            key: 'scrape',
            icon: <DownloadOutlined />,
            label: '抓取信息',
            onClick: () => {
              setScrapingProject(record)
              // Auto-select unscraped types
              const types: string[] = []
              if (!record.email || record.email_scrape_status === 'NOT_SCRAPED') {
                types.push('email')
              }
              if (!record.twitter_handle || record.twitter_scrape_status === 'NOT_SCRAPED') {
                types.push('twitter')
              }
              if (!record.telegram_group || record.telegram_scrape_status === 'NOT_SCRAPED') {
                types.push('telegram')
              }
              setSelectedScrapeTypes(types.length > 0 ? types : ['email', 'twitter', 'telegram'])
              setQuickScrapeModalOpen(true)
            },
          },
          {
            key: 'conflict_check',
            icon: conflictChecking[record.id] ? <ReloadOutlined spin /> : <WarningOutlined />,
            label: conflictChecking[record.id] ? '检测中...' : '检查CRM冲突',
            disabled: conflictChecking[record.id],
            onClick: async () => {
              setConflictChecking(prev => ({ ...prev, [record.id]: true }))
              try {
                const result = await projectApi.checkConflict([record.id])
                if (result.status === 'skipped') {
                  message.warning('CRM 未配置，无法检测冲突')
                } else {
                  message.success('冲突检测完成')
                  refetch()
                }
              } catch (error: any) {
                message.error(`检测失败: ${error.message || '未知错误'}`)
              } finally {
                setConflictChecking(prev => ({ ...prev, [record.id]: false }))
              }
            },
          },
          { type: 'divider' },
          // 根据项目状态显示冷池操作
          record.status === 'cold_pool'
            ? {
                key: 'restore',
                icon: <InboxOutlined />,
                label: '从冷池恢复',
                onClick: () => {
                  Modal.confirm({
                    title: '确认从冷池恢复？',
                    content: `将项目 ${record.name} 从冷池恢复后，可继续触达。`,
                    onOk: () => restoreFromColdPool.mutate(record.id),
                  })
                },
              }
            : {
                key: 'cold_pool',
                icon: <InboxOutlined />,
                label: '移入冷池',
                onClick: () => {
                  Modal.confirm({
                    title: '确认移入冷池？',
                    content: `将项目 ${record.name} 移入冷池后，将暂停触达。`,
                    onOk: () => moveToColdPool.mutate(record.id),
                  })
                },
              },
          {
            key: 'delete',
            icon: <DeleteOutlined />,
            label: '删除',
            danger: true,
            onClick: () => {
              Modal.confirm({
                title: '确认删除？',
                content: `确定要删除项目 ${record.name} 吗？此操作不可恢复。`,
                onOk: () => deleteProject.mutate(record.id),
              })
            },
          },
        ]
        return (
          <Dropdown menu={{ items: menuItems }} trigger={['click']}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        )
      },
    },
  ]

  // 批量操作菜单
  const batchMenuItems: MenuProps['items'] = [
    {
      key: 'batch_scrape',
      icon: <DownloadOutlined />,
      label: '批量抓取',
      onClick: handleBatchScrapeSelected,
    },
    {
      key: 'rescore',
      icon: <ReloadOutlined />,
      label: '批量重新评分',
      onClick: () => {
        if (selectedRowKeys.length === 0) {
          message.warning('请先选择项目')
          return
        }
        batchRescore.mutate(selectedRowKeys as number[])
      },
    },
    {
      key: 'outreach',
      icon: <SendOutlined />,
      label: '批量触达',
      onClick: () => {
        if (selectedRowKeys.length === 0) {
          message.warning('请先选择项目')
          return
        }
        navigate(`/outreach?project_ids=${selectedRowKeys.join(',')}`)
      },
    },
    { type: 'divider' },
    {
      key: 'batch_delete',
      icon: <DeleteOutlined />,
      label: '批量删除',
      danger: true,
      onClick: () => {
        if (selectedRowKeys.length === 0 && !selectAllPages) {
          message.warning('请先选择项目')
          return
        }
        const count = selectAllPages ? (data?.total || 0) : selectedRowKeys.length
        Modal.confirm({
          title: '确认批量删除',
          content: selectAllPages
            ? `确定要删除全部 ${count} 个项目吗？此操作不可恢复，关联的触达记录和回复也会一并删除。`
            : `确定要删除选中的 ${count} 个项目吗？此操作不可恢复，关联的触达记录和回复也会一并删除。`,
          okText: '删除',
          okType: 'danger',
          cancelText: '取消',
          onOk: async () => {
            if (selectAllPages) {
              // 全选模式：传 select_all + 当前筛选条件
              await projectApi.batchDelete({
                select_all: true,
                filters: {
                  status: status || undefined,
                  search: searchText || undefined,
                  scrape_filter: scrapeFilter || undefined,
                },
              })
              setSelectAllPages(false)
              setSelectedRowKeys([])
              refetch()
              message.success(`已删除 ${count} 个项目`)
            } else {
              batchDelete.mutate(selectedRowKeys.map(String), {
                onSuccess: () => setSelectedRowKeys([]),
              })
            }
          },
        })
      },
    },
  ]

  // 导出处理
  const handleExport = async () => {
    try {
      const blob = await projectApi.exportProjects({
        status: status || undefined,
        search: searchText || undefined,
      })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `projects_${dayjs().format('YYYYMMDD_HHmmss')}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
      message.success('导出成功')
    } catch (error) {
      message.error('导出失败')
    }
  }

  // 导入配置
  const uploadProps: UploadProps = {
    name: 'file',
    accept: '.csv,.xlsx',
    showUploadList: false,
    beforeUpload: async (file) => {
      try {
        const result = await projectApi.importProjects(file)
        message.success(`导入完成：成功 ${result.success}，失败 ${result.failed}`)
        refetch()
        setImportModalOpen(false)
      } catch (error) {
        message.error('导入失败')
      }
      return false
    },
  }

  // GBR onBlur check for twitter_handle
  const handleTwitterHandleBlur = async (handle: string) => {
    const trimmed = handle.trim()
    if (!trimmed) {
      setGbrResult(null)
      return
    }
    setGbrChecking(true)
    try {
      const result = await projectApi.gbrCheck(trimmed)
      if (!result.allowed && !result.skipped) {
        setGbrResult({ allowed: false, crm_bd: result.crm_bd })
        message.warning(`@${trimmed} 在 CRM 中已归属于 ${result.crm_bd}，创建后将被阻止`)
      } else {
        setGbrResult({ allowed: true })
      }
    } catch {
      setGbrResult(null)
    } finally {
      setGbrChecking(false)
    }
  }

  // 从 CRM 同步归属（仅更新冲突状态）
  const handleSyncFromCrm = async () => {
    setSyncingFromCrm(true)
    try {
      const result = await projectApi.syncFromCrm()
      if (result.status === 'skipped') {
        message.info('CRM 未配置，跳过同步')
      } else {
        message.success(
          `CRM 归属同步完成：共 ${result.total} 个项目，${result.conflicts_found} 个发现冲突，${result.errors} 个错误`
        )
        refetch()
      }
    } catch {
      message.error('CRM 同步失败')
    } finally {
      setSyncingFromCrm(false)
    }
  }

  // 从 CRM 导入项目（创建 BB 中不存在的项目）
  const handleImportFromCrm = async () => {
    setImportingFromCrm(true)
    try {
      const result = await projectApi.importFromCrm()
      if (result.status === 'skipped') {
        message.info(result.message || 'CRM 未配置，跳过导入')
        return
      }
      setImportResult(result)
      if (result.created > 0 || result.updated > 0) {
        refetch()
      }
    } catch {
      message.error('从 CRM 导入失败')
    } finally {
      setImportingFromCrm(false)
    }
  }

  // 创建项目
  const handleCreate = async (values: any) => {
    // 官网和 Twitter 二选一
    const hasWebsite = values.website && values.website.trim()
    const hasTwitter = values.twitter_handle && values.twitter_handle.trim()
    if (!hasWebsite && !hasTwitter) {
      message.warning('官网和 Twitter 至少填写一个')
      return
    }
    // telegram_admins: ['handler1', 'handler2'] → [{username, role, source}]
    if (values.telegram_admins && Array.isArray(values.telegram_admins)) {
      values.telegram_admins = values.telegram_admins.map((h: string) => ({
        username: h.replace(/^@/, '').trim(),
        role: 'admin',
        source: 'manual',
      }))
    }
    try {
      await createProject.mutateAsync(values)
      setCreateModalOpen(false)
      form.resetFields()
      setGbrResult(null)
    } catch (error) {
      // 错误已在API层处理
    }
  }

  return (
    <div className="fade-in">
      {/* 页面标题 */}
      <div className="page-header">
        <h1>项目管理</h1>
        <Space>
          <Button icon={<ImportOutlined />} onClick={() => setImportModalOpen(true)}>
            导入
          </Button>
          <Button icon={<ExportOutlined />} onClick={handleExport}>
            导出
          </Button>
          <Button
            icon={<ReloadOutlined />}
            loading={importingFromCrm}
            onClick={handleImportFromCrm}
          >
            从CRM导入项目
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>
            新建项目
          </Button>
        </Space>
      </div>

      {/* 筛选区 */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Search
            placeholder="搜索项目名称或网站"
            allowClear
            style={{ width: 250 }}
            value={searchText}
            onChange={(e) => setFilter('searchText', e.target.value)}
            onSearch={() => refetch()}
            prefix={<SearchOutlined />}
          />
          <Select
            placeholder="状态筛选"
            allowClear
            style={{ width: 150 }}
            value={status}
            onChange={(value) => setFilter('status', value)}
            options={Object.entries(statusConfig).map(([key, value]) => ({
              value: key,
              label: value.text,
            }))}
          />
          <Select
            placeholder="信息抓取状态"
            allowClear
            style={{ width: 150 }}
            value={scrapeFilter}
            onChange={(value) => setFilter('scrapeFilter', value)}
            options={[
              { value: 'email_not_scraped', label: '邮箱未抓取' },
              { value: 'twitter_not_scraped', label: '推特未抓取' },
              { value: 'telegram_not_scraped', label: 'TG未抓取' },
              { value: 'any_not_scraped', label: '存在未抓取' },
              { value: 'all_scraped', label: '全部已抓取' },
            ]}
          />
          <Select
            placeholder="CRM冲突状态"
            allowClear
            style={{ width: 140 }}
            value={crmConflictFilter}
            onChange={(value) => setFilter('crmConflictFilter', value)}
            options={[
              { value: 'conflict_detected', label: '冲突待处理' },
              { value: 'conflict_resolved', label: '冲突已解决' },
              { value: 'pending_check', label: '检测中' },
              { value: 'check_failed', label: '检测失败' },
            ]}
          />
          <Button icon={<FilterOutlined />} onClick={resetFilters}>
            重置
          </Button>
          <Dropdown menu={{ items: batchMenuItems }} disabled={selectedRowKeys.length === 0 && !selectAllPages}>
            <Button>
              批量操作 {selectAllPages ? `(全部 ${data?.total || 0})` : selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ''}
            </Button>
          </Dropdown>
        </Space>
      </Card>

      {/* 表格 */}
      <Card>
        <Table
          columns={columns}
          dataSource={data?.items}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 1500 }}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => { setSelectedRowKeys(keys); setSelectAllPages(false); },
            selections: [
              Table.SELECTION_ALL,
              Table.SELECTION_INVERT,
              Table.SELECTION_NONE,
              {
                key: 'select_all_pages',
                text: `全选所有页 (${data?.total || 0} 个项目)`,
                onSelect: () => {
                  setSelectAllPages(true)
                  // 选中当前页所有行作为视觉反馈
                  setSelectedRowKeys((data?.items || []).map(p => p.id))
                },
              },
            ],
          }}
          pagination={{
            current: page,
            pageSize,
            total: data?.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (p, ps) => {
              setPage(p)
              if (ps !== pageSize) setPageSize(ps)
            },
          }}
          onChange={(_, __, sorter) => {
            if (!Array.isArray(sorter) && sorter.field) {
              setFilter('sortBy', sorter.field as string)
              setFilter('sortOrder', sorter.order === 'ascend' ? 'asc' : 'desc')
            }
          }}
        />
      </Card>

      {/* 创建项目弹窗 */}
      <Modal
        title="新建项目"
        open={createModalOpen}
        onCancel={() => {
          setCreateModalOpen(false)
          form.resetFields()
        }}
        onOk={() => form.submit()}
        confirmLoading={createProject.isPending}
      >
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.Item
            name="name"
            label="项目名称"
            rules={[{ required: true, message: '请输入项目名称' }]}
          >
            <Input placeholder="请输入项目名称" />
          </Form.Item>
          <Form.Item
            name="website"
            label="网站"
            rules={[
              { type: 'url', message: '请输入有效的URL' },
            ]}
          >
            <Input placeholder="https://example.com" />
          </Form.Item>
          <Form.Item
            name="twitter_handle"
            label="Twitter"
            validateStatus={gbrResult && !gbrResult.allowed ? 'warning' : undefined}
            help={
              gbrResult && !gbrResult.allowed
                ? `⚠️ 该 handle 在 CRM 中已归属于 ${gbrResult.crm_bd}，创建可能被拒绝`
                : undefined
            }
          >
            <Input
              placeholder="handle（不含@符号）"
              loading={gbrChecking}
              onBlur={(e) => handleTwitterHandleBlur(e.target.value)}
              onChange={() => setGbrResult(null)}
            />
          </Form.Item>
          <Form.Item name="telegram_group" label="Telegram群组">
            <Input placeholder="https://t.me/group" />
          </Form.Item>
          <Form.Item name="telegram_admins" label="管理员 Telegram Handler">
            <Select
              mode="tags"
              placeholder="输入 handler（如 admin1），回车添加"
              tokenSeparators={[',', ' ', '\n']}
            />
          </Form.Item>
          <Form.Item name="email" label="邮箱">
            <Input placeholder="contact@example.com" />
          </Form.Item>
          <Form.Item name="category" label="分类">
            <Select
              placeholder="选择分类"
              options={[
                { value: 'DeFi', label: 'DeFi' },
                { value: 'NFT', label: 'NFT' },
                { value: 'GameFi', label: 'GameFi' },
                { value: 'Infrastructure', label: 'Infrastructure' },
                { value: 'DAO', label: 'DAO' },
                { value: 'Other', label: 'Other' },
              ]}
            />
          </Form.Item>
          <Form.Item name="notes" label="备注">
            <Input.TextArea rows={3} placeholder="备注信息" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 导入弹窗 */}
      <Modal
        title="导入项目"
        open={importModalOpen}
        onCancel={() => setImportModalOpen(false)}
        footer={null}
      >
        <div style={{ marginBottom: 16 }}>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => projectApi.downloadImportTemplate()}
          >
            下载导入模板
          </Button>
          <span style={{ marginLeft: 8, color: '#999', fontSize: 12 }}>
            首次导入请先下载模板，按模板格式填写数据
          </span>
        </div>
        <Upload.Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
          <p className="ant-upload-hint">
            支持 CSV / XLSX 格式，文件需包含 website 列（必填），name / category / email / twitter_handle / telegram_group 列（选填）
          </p>
        </Upload.Dragger>
      </Modal>

      {/* 从CRM导入结果弹窗 */}
      <Modal
        title="从 CRM 导入项目 - 结果"
        open={!!importResult}
        onCancel={() => setImportResult(null)}
        footer={[
          <Button key="close" type="primary" onClick={() => setImportResult(null)}>
            关闭
          </Button>
        ]}
        width={600}
      >
        {importResult && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div style={{ textAlign: 'center', padding: '12px 0', background: '#f6ffed', borderRadius: 4 }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#52c41a' }}>{importResult.total_from_crm}</div>
                <div style={{ fontSize: 12, color: '#888' }}>CRM 共 N 项目</div>
              </div>
              <div style={{ textAlign: 'center', padding: '12px 0', background: '#e6f4ff', borderRadius: 4 }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#1677ff' }}>{importResult.created}</div>
                <div style={{ fontSize: 12, color: '#888' }}>新建</div>
              </div>
              <div style={{ textAlign: 'center', padding: '12px 0', background: '#fffbe6', borderRadius: 4 }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#faad14' }}>{importResult.updated}</div>
                <div style={{ fontSize: 12, color: '#888' }}>已更新</div>
              </div>
              <div style={{ textAlign: 'center', padding: '12px 0', background: '#fff2f0', borderRadius: 4 }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#ff4d4f' }}>{importResult.skipped_conflict}</div>
                <div style={{ fontSize: 12, color: '#888' }}>归属冲突</div>
              </div>
            </div>

            {importResult.created_items.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>新建项目（{importResult.created_items.length}）：</div>
                <div style={{ maxHeight: 150, overflowY: 'auto', background: '#f9f9f9', padding: 8, borderRadius: 4 }}>
                  {importResult.created_items.map((item, i) => (
                    <div key={i} style={{ fontSize: 13, padding: '2px 0' }}>
                      <span style={{ color: '#1677ff' }}>@{item.twitter_handle}</span>
                      {' '}{item.project_name !== item.twitter_handle ? `（${item.project_name}）` : ''}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {importResult.conflict_items.length > 0 && (
              <div>
                <div style={{ fontWeight: 600, marginBottom: 6, color: '#ff4d4f' }}>
                  归属冲突（{importResult.conflict_items.length}）— 已跳过，请联系管理员：
                </div>
                <div style={{ maxHeight: 120, overflowY: 'auto', background: '#fff2f0', padding: 8, borderRadius: 4 }}>
                  {importResult.conflict_items.map((item, i) => (
                    <div key={i} style={{ fontSize: 13, padding: '2px 0' }}>
                      <span style={{ color: '#ff4d4f' }}>@{item.twitter_handle}</span>
                      {' '}— BB 归属：{item.bb_owner}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 快速抓取弹窗 */}
      <Modal
        title={`抓取信息 - ${scrapingProject?.name || ''}`}
        open={quickScrapeModalOpen}
        onCancel={() => {
          setQuickScrapeModalOpen(false)
          setScrapingProject(null)
        }}
        onOk={handleQuickScrape}
        confirmLoading={scraping}
        okText="开始抓取"
        cancelText="取消"
      >
        <div style={{ marginBottom: 16 }}>
          <p>选择要抓取的信息类型：</p>
          <Checkbox.Group
            value={selectedScrapeTypes}
            onChange={(values) => setSelectedScrapeTypes(values as string[])}
          >
            <Space direction="vertical">
              <Checkbox value="email">邮箱信息</Checkbox>
              <Checkbox value="twitter">推特账号</Checkbox>
              <Checkbox value="telegram">Telegram群组</Checkbox>
            </Space>
          </Checkbox.Group>
        </div>
        <div style={{ color: '#666', fontSize: 12 }}>
          <p>💡 提示：</p>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>抓取将从项目网站自动提取相关信息</li>
            <li>抓取完成后，可在触达中心创建触达任务</li>
            <li>如果抓取失败，请检查网站是否可访问</li>
          </ul>
        </div>
      </Modal>
    </div>
  )
}

export default ProjectList
